/* rv-sticky-atc: the sticky add bar (DESIGN-SYSTEM.md §8.16). Markup: snippets/rv-sticky-atc.liquid.
   Shows once the buy box's main add button ([data-rv-add]) has left the screen upward; with data-early (a media
   query, phones and tablets) whenever that button isn't on screen, from the top of the page too; follows the page
   flavor (rv:flavor-change / rv:buy-change); adds through the buy box ("rv:sticky-add", cancelable);
   hides while the cart drawer, the mobile menu or the email popup is open, and while a section with its own
   add button is on screen (the face-off's cards, the PDP cross-sell, the Short List's resolve at its last step,
   and the elements in data-hide-near), so there's never a second primary for the other flavor in the thumb zone
   (critique C-3). data-mq (a media query): the bar exists only while it matches; otherwise it is [hidden], so the
   page reserves nothing for it. The same under 500px of screen height (A11Y-14, 2026-10-12).
   Cards mode (data-mode="cards", the shop page's Peach / Raspberry / Duo cards inside the target): it shows once the
   LAST card's add button has left the top, and follows only what the visitor did on this page (D9): the card
   marked by the shop page's hash or link ("rv:shop-target" detail { id }, or li.is-target when the bar connects),
   a tap (not a scroll that starts on a card) or focus inside a card, or a plan change in a card (rv:buy-change /
   rv:flavor-change whose detail.source is a card; events from outside the cards are ignored). Until then it shows the server-rendered "Pick your
   flavor" state, and its button scrolls to the cards and focuses them. It never dispatches a flavor event. */
(() => {
  if (customElements.get('rv-sticky-atc')) return;
  const D = document;
  const INLINE = '.rv-taste__duel, .rv-cross-sell';
  const RESOLVE = 'rv-short-list .rv-result__resolve';

  class RvStickyAtc extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      let data = {};
      try {
        data = JSON.parse(this.querySelector('[data-rv-sticky-data]').textContent) || {};
      } catch (e) {}
      this.data = data;

      this.btn = this.querySelector('[data-rv-sticky-btn]');
      this.nameEl = this.querySelector('[data-rv-sticky-name]');
      this.priceEl = this.querySelector('[data-rv-sticky-price]');
      this.labelEl = this.querySelector('[data-rv-sticky-label]');
      this.shortEl = this.querySelector('[data-rv-sticky-short]');
      this.inner = this.querySelector('.rv-sticky-atc__inner');
      this.swatch = this.querySelector('.rv-sticky-atc__swatch');
      this.full = this.btn.disabled ? '' : this.labelEl.textContent; /* the long label, kept for fit() */

      this.targetSel = this.dataset.target || '#buy';
      try {
        this.target = D.querySelector(this.targetSel);
      } catch (e) {
        this.target = null;
      }
      /* no buy box on this page, or another bar already serves this one (e.g. the footer's homepage bar
         plus one rendered by a section): stay hidden, so the page doesn't reserve space for it either */
      const twin = [...D.querySelectorAll('rv-sticky-atc')].some(
        (el) => el !== this && el.active && el.targetSel === this.targetSel
      );
      if (!this.target || twin) {
        this.hidden = true;
        return;
      }
      this.active = true;
      this.hidden = false;

      this.cards = this.dataset.mode === 'cards';
      /* cards mode watches the LAST card's add button (the Duo's), so the bar stays down over every card */
      const adds = this.cards ? this.target.querySelectorAll('[data-rv-add]') : null;
      this.watch = (adds && adds[adds.length - 1]) || this.target.querySelector('[data-rv-add]') || this.target;
      this.past = false;
      this.blocked = false;
      this.busy = false;

      if (this.cards) {
        this.initCards();
      } else {
        /* start state: the buy box's current selection, else the flavor on <html>, else the default */
        const box = this.target.matches('rv-buy-box') ? this.target : this.target.querySelector('rv-buy-box');
        const state = box && box.state;
        if (state && state.handle) this.apply(state);
        else this.apply({ handle: D.documentElement.getAttribute('data-rv-flavor') || this.dataset.default });

        this.onFlavor = (e) => this.apply(e.detail || {});
        D.addEventListener('rv:flavor-change', this.onFlavor);
        D.addEventListener('rv:buy-change', this.onFlavor);
      }

      this.onDone = () => this.setBusy(false);
      D.addEventListener('rv:cart-add', this.onDone);
      D.addEventListener('rv:cart-error', this.onDone);

      this.btn.addEventListener('click', () => this.add());

      /* the bar's height depends on the width and the font (see fit()) */
      this.w = innerWidth;
      this.onResize = () => {
        if (this.w === innerWidth) return;
        this.w = innerWidth;
        this.fit();
      };
      addEventListener('resize', this.onResize);
      if (D.fonts) D.fonts.ready.then(() => this.fit());

      /* the root reaches far below the screen, so the state flips only when the button crosses the TOP edge */
      this.io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          });
          this.sync();
        },
        { rootMargin: '0px 0px 1000% 0px' }
      );
      this.io.observe(this.watch);

      /* Phones and tablets (data-early, Cris 2026-10-30: "an add to cart at the bottom of the page that's sticky for
         mobile"): the bar is also up before the visitor reaches the buy box (on a phone the product page's add button
         sits about two screens down), so an add button is always in reach. It steps aside only while the main
         button itself is on screen. Desktop keeps "once the button has left the top". */
      this.seenIo = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this.onScreen = entry.isIntersecting;
        });
        this.sync();
      });
      this.seenIo.observe(this.watch);

      /* Sections that carry their own add button. The test is relative to the screen, not the section: a section
         counts once it reaches the top 75% of the screen, until it has left the top (a ratio threshold never
         fires for the ~2,100px taste fields on a 660px iPhone screen). Whole sections are observed, so the bar
         hides once for both taste fields instead of flickering between them. */
      this.inline = new Set();
      const track = (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.inline.add(entry.target);
          else this.inline.delete(entry.target);
        });
        this.sync();
      };
      this.inlineIo = new IntersectionObserver(track, { rootMargin: '0px 0px -25% 0px' });
      D.querySelectorAll(INLINE).forEach((el) => this.inlineIo.observe(el));
      /* the page's own extra sections to hide over (data-hide-near, e.g. the shop page's final CTA) */
      if (this.dataset.hideNear) {
        try {
          D.querySelectorAll(this.dataset.hideNear).forEach((el) => this.inlineIo.observe(el));
        } catch (e) {}
      }
      /* the Short List's resolve sits low in its pinned stage: any part of it on screen counts */
      this.resolveIo = new IntersectionObserver(track);
      D.querySelectorAll(RESOLVE).forEach((el) => this.resolveIo.observe(el));

      /* overlays: Dawn's cart drawer (.active), the header menu (.menu-open), the popup (.rv-open) */
      this.mo = new MutationObserver(() => this.sync());
      const watch = (el) => el && this.mo.observe(el, { attributes: true, attributeFilter: ['class', 'open'] });
      watch(D.querySelector('cart-drawer'));
      watch(D.querySelector('.section-header'));
      watch(D.querySelector('#Details-menu-drawer-container'));
      watch(D.querySelector('.rv-overlay'));
      watch(D.body);
      /* the Short List's resolve only counts once it's shown (data-done, at the last step) */
      D.querySelectorAll('rv-short-list').forEach((el) =>
        this.mo.observe(el, { attributes: true, attributeFilter: ['data-done', 'class'] })
      );

      /* data-mq (e.g. the shop page's "(max-width: 989px)"): outside it the bar is [hidden], never shown, and
         html[data-rv-sticky] is cleared; the footer then reserves nothing for it (rv-chrome.css).
         Short screens too (A11Y-14, compliance audit 2026-10-12): at 400% zoom (a 320x256 viewport) the sticky header
         and this bar covered 142 of 256px; a phone on its side is about 390px tall. Under 500px of height the bar is
         [hidden] the same way (the buy box's own button is still on the page) and comes back when there's room. */
      if (window.matchMedia) {
        if (this.dataset.mq) this.mql = window.matchMedia(this.dataset.mq);
        this.lowMql = window.matchMedia('(max-height: 500px)');
        if (this.dataset.early && !this.cards) this.earlyMql = window.matchMedia(this.dataset.early);
        this.onMq = () => {
          this.mqOff = Boolean((this.mql && !this.mql.matches) || this.lowMql.matches);
          this.early = Boolean(this.earlyMql && this.earlyMql.matches);
          this.hidden = this.mqOff;
          this.sync();
          if (!this.mqOff) this.fit();
        };
        [this.mql, this.lowMql, this.earlyMql].forEach((m) => {
          if (!m) return;
          if (m.addEventListener) m.addEventListener('change', this.onMq);
          else if (m.addListener) m.addListener(this.onMq);
        });
        this.onMq();
      }
    }

    /* cards mode: the "Pick your flavor" state until the visitor picks a card on this page (D9) */
    initCards() {
      this.box = null;
      this.addSel = '';
      this.pickState();
      this.onCard = (e) => {
        const d = e.detail || {};
        const src = d.source;
        if (!(src instanceof Element) || !this.target.contains(src)) return;
        /* the menu's "Subscribe & save" switches every card at once (this.bulk): that is not a pick of the
           other cards (and the menu's following focus on the first card's radio is not a pick either: onTouch) */
        if (src === this.box) this.apply(d);
        else if (!this.bulk) this.setActive(src);
      };
      D.addEventListener('rv:flavor-change', this.onCard);
      D.addEventListener('rv:buy-change', this.onCard);
      /* window, capture phase: runs before the cards' own rv:plan-pick listeners on document */
      this.onBulk = () => {
        this.bulk = true;
        setTimeout(() => (this.bulk = false));
      };
      addEventListener('rv:plan-pick', this.onBulk, true);
      this.onTarget = (e) => {
        const id = String((e.detail || {}).id || '');
        const li = id ? D.getElementById(id) : null;
        if (li && this.target.contains(li)) this.setActive(li.querySelector('rv-buy-box'));
      };
      D.addEventListener('rv:shop-target', this.onTarget);
      this.onTouch = (e) => {
        /* the menu's "Subscribe & save" focuses the first card's Subscribe radio right after rv:plan-pick (still
           this.bulk): a focus moved by script is not the visitor's pick either */
        if (e.type === 'focusin' && this.bulk) return;
        const box = e.target && e.target.closest ? e.target.closest('rv-buy-box') : null;
        if (box && this.target.contains(box)) this.setActive(box);
      };
      /* A tap, not a touch: on phones pointerdown also starts every scroll or gallery swipe over a card (then
         pointercancel), so the card counts only when the same pointer comes up within 10px of where it went down.
         (A click listener would work too, but iOS would draw its gray tap highlight over the whole cards list.) */
      let down = null;
      this.onDown = (e) => {
        down = e.isPrimary ? { target: e.target, id: e.pointerId, x: e.clientX, y: e.clientY } : null;
      };
      this.onCancel = () => {
        down = null;
      };
      this.onUp = (e) => {
        const d0 = down;
        down = null;
        if (!d0 || e.pointerId !== d0.id) return;
        if (Math.abs(e.clientX - d0.x) < 10 && Math.abs(e.clientY - d0.y) < 10) {
          this.onTouch({ type: 'tap', target: d0.target });
        }
      };
      this.target.addEventListener('pointerdown', this.onDown, { passive: true });
      this.target.addEventListener('pointerup', this.onUp, { passive: true });
      this.target.addEventListener('pointercancel', this.onCancel, { passive: true });
      this.target.addEventListener('focusin', this.onTouch);
      /* the shop page may have marked the hash card before this bar connected */
      const marked = this.target.querySelector('.rv-shop__item.is-target rv-buy-box');
      if (marked) this.setActive(marked);
    }

    pickState() {
      this.dataset.flavor = 'duo';
      this.nameEl.textContent = this.dataset.pickName || this.nameEl.textContent;
      /* the pick name ("Peach, Raspberry or the Duo") wraps to a second line rather than being cut off */
      this.nameEl.style.whiteSpace = 'normal';
      this.priceEl.textContent = '';
      this.btn.disabled = false;
      this.btn.removeAttribute('aria-disabled');
      this.full = this.dataset.pickLabel || this.labelEl.textContent;
      this.labelEl.textContent = this.full;
      this.shortEl.textContent = this.full;
      this.fit();
    }

    /* cards mode: follow card `box` (its state, its colors on the bar only; nothing is dispatched) */
    setActive(box) {
      const st = box && box.state;
      if (!st || !st.handle) return;
      const li = box.closest('li[id]');
      this.box = box;
      this.addSel = li && this.target.contains(li) ? `#${li.id}` : '';
      ['accent', 'deep', 'tint'].forEach((t) => {
        if (st[t]) this.style.setProperty(`--rv-flavor-${t}`, st[t]);
        else this.style.removeProperty(`--rv-flavor-${t}`);
      });
      this.nameEl.style.whiteSpace = '';
      this.apply(st);
    }

    disconnectedCallback() {
      if (this.io) this.io.disconnect();
      if (this.seenIo) this.seenIo.disconnect();
      if (this.inlineIo) this.inlineIo.disconnect();
      if (this.resolveIo) this.resolveIo.disconnect();
      if (this.mo) this.mo.disconnect();
      if (this.onResize) removeEventListener('resize', this.onResize);
      if (this.onMq) {
        [this.mql, this.lowMql, this.earlyMql].forEach((m) => {
          if (!m) return;
          if (m.removeEventListener) m.removeEventListener('change', this.onMq);
          else if (m.removeListener) m.removeListener(this.onMq);
        });
      }
      if (this.onCard) {
        D.removeEventListener('rv:flavor-change', this.onCard);
        D.removeEventListener('rv:buy-change', this.onCard);
        D.removeEventListener('rv:shop-target', this.onTarget);
        removeEventListener('rv:plan-pick', this.onBulk, true);
        if (this.target) {
          this.target.removeEventListener('pointerdown', this.onDown);
          this.target.removeEventListener('pointerup', this.onUp);
          this.target.removeEventListener('pointercancel', this.onCancel);
          this.target.removeEventListener('focusin', this.onTouch);
        }
        this.onCard = null;
      }
      D.removeEventListener('rv:flavor-change', this.onFlavor);
      D.removeEventListener('rv:buy-change', this.onFlavor);
      D.removeEventListener('rv:cart-add', this.onDone);
      D.removeEventListener('rv:cart-error', this.onDone);
      this.ready = false;
      this.active = false;
      if (this.on) D.documentElement.removeAttribute('data-rv-sticky');
      this.on = false;
    }

    overlayOpen() {
      return Boolean(
        D.querySelector('cart-drawer.active') ||
          D.querySelector('.section-header.menu-open') ||
          D.querySelector('#Details-menu-drawer-container[open]') ||
          D.querySelector('.rv-overlay.rv-open')
      );
    }

    /* an in-section add button is on screen (the resolve counts only while the Short List shows it) */
    inlineOpen() {
      for (const el of this.inline || []) {
        const sl = el.closest('rv-short-list');
        if (sl && sl.classList.contains('is-motion') && !sl.hasAttribute('data-done')) continue;
        return true;
      }
      return false;
    }

    sync() {
      const reached = this.early ? this.onScreen === false : this.past;
      const on = !this.mqOff && reached && !this.overlayOpen() && !this.inlineOpen();
      if (on === this.on) return;
      this.on = on;
      this.classList.toggle('is-on', on);
      this.setAttribute('aria-hidden', on ? 'false' : 'true');
      this.toggleAttribute('inert', !on);
      /* WCAG 2.4.11: while the bar is up, <html> reserves it as scroll padding (rv-sticky-atc.css), so the
         browser scrolls a keyboard-focused element clear of the bar instead of leaving it underneath */
      if (on) D.documentElement.setAttribute('data-rv-sticky', 'on');
      else D.documentElement.removeAttribute('data-rv-sticky');
      if (!on) this.setBusy(false);
    }

    apply(d) {
      const handle = String(d.handle || this.handle || this.dataset.default || '');
      const entry = this.data[handle] || this.data[handle.toLowerCase()] || {};
      const isDuo = handle === 'duo' || Boolean(entry.duo);
      this.handle = handle;

      const name = entry.name || d.name || '';
      let price = d.price || entry.price || '';
      /* a subscription (the buy box's state): d.price = the first order's price, d.then = later orders' price when
         it differs, d.every = the frequency words ("a month", "every 2 months"; blank when unknown: no words are
         guessed). "$26.24, then $27.99 a month" / "$26.24 every 2 months" / "$26.24". No-break spaces keep each
         part whole ("then $27.99", "a month"), so a narrow bar wraps between the parts and never hides one under
         the button (the price span allows wrapping); fit() keeps it to 2 lines. */
      if (d.plan === 'sub' && d.price) {
        const nb = (t) => String(t).replace(/ /g, '\u00a0');
        price = [d.then ? `${d.price},` : d.price, d.then ? `then ${d.then}` : '', d.every || '']
          .filter(Boolean)
          .map(nb)
          .join(' ');
      }
      const available = typeof d.available === 'boolean' ? d.available : entry.available !== false;
      /* cards mode: the card's own button words ("Add Peach") */
      const label =
        (this.cards && d.label) ||
        (isDuo ? entry.label || 'Add the Duo' : entry.label || (name ? `Add ${name} to cart` : ''));

      this.dataset.flavor = isDuo ? 'duo' : 'single';
      if (name) this.nameEl.textContent = name;
      if (price) this.priceEl.textContent = price;

      if (available) {
        this.btn.disabled = false;
        this.btn.removeAttribute('aria-disabled');
        if (label) this.full = label;
        if (this.full) this.labelEl.textContent = this.full;
        this.shortEl.textContent = isDuo ? entry.label || 'Add the Duo' : 'Add to cart';
      } else {
        const soldOut = this.dataset.soldOut || 'Sold out';
        this.btn.disabled = true;
        this.btn.setAttribute('aria-disabled', 'true');
        this.labelEl.textContent = soldOut;
        this.shortEl.textContent = soldOut;
      }
      this.fit();
    }

    /* The page keeps one fixed height free for the bar, --rv-sticky-reserve (84px, snippets/rv-tokens.liquid): the
       footer's bottom padding, the scroll padding, the popup's corner tab and the Short List's stage are all laid out
       against it (the Short List measures its fit once, so the reserve itself must not change under it). So the bar
       must not grow past it. A subscription with a first-order price ("$26.24, then $27.99 a month") wraps to 3
       lines beside "Add Raspberry to cart" at about 390-500px wide: then the button takes its short label ("Add to
       cart", as under 390px), which leaves the price 2 lines. If that's not enough (the Duo's "Add the Duo" at
       360px), the decorative swatch steps aside too. The flavor's name stays in the bar, and the bar's text is the
       button's description. The safe area is left out: the bar and everything that reserves it add it. */
    fit() {
      if (!this.active || !this.inner || this.busy || this.btn.disabled || !this.full) return;
      const reserve = parseFloat(getComputedStyle(D.documentElement).getPropertyValue('--rv-sticky-reserve')) || 84;
      const cs = getComputedStyle(this.inner);
      const safe = Math.max(0, parseFloat(cs.paddingBottom) - parseFloat(cs.paddingTop)) || 0;
      const tall = () => this.getBoundingClientRect().height - safe > reserve + 0.5;
      this.labelEl.textContent = this.full;
      if (this.swatch) this.swatch.style.display = '';
      if (!tall()) return;
      this.labelEl.textContent = this.shortEl.textContent;
      if (this.swatch && tall()) this.swatch.style.display = 'none';
    }

    setBusy(busy) {
      if (!this.btn || this.busy === busy) return;
      this.busy = busy;
      clearTimeout(this.busyTimer);
      if (busy) {
        this.btn.style.minWidth = `${this.btn.offsetWidth}px`;
        this.btn.setAttribute('aria-busy', 'true');
        this.savedLabel = [this.labelEl.textContent, this.shortEl.textContent];
        this.labelEl.textContent = this.dataset.adding || 'Adding…';
        this.shortEl.textContent = this.dataset.adding || 'Adding…';
        this.busyTimer = setTimeout(() => this.setBusy(false), 10000);
      } else {
        this.btn.removeAttribute('aria-busy');
        this.btn.style.minWidth = '';
        if (this.savedLabel) {
          this.labelEl.textContent = this.savedLabel[0];
          this.shortEl.textContent = this.savedLabel[1];
          this.savedLabel = null;
        }
        this.fit();
      }
    }

    add() {
      if (this.busy || this.btn.disabled) return;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      /* cards mode, no card picked yet ("Pick your flavor"): take the visitor to the cards */
      if (this.cards && !this.box) {
        this.target.scrollIntoView({ block: 'start', behavior: reduce ? 'auto' : 'smooth' });
        if (typeof this.target.focus === 'function') this.target.focus({ preventScroll: true });
        return;
      }
      const event = new CustomEvent('rv:sticky-add', {
        bubbles: true,
        cancelable: true,
        /* cards mode: the picked card's <li> ('#raspberry'), so that card adds its own plan and frequency */
        detail: { target: (this.cards && this.addSel) || this.targetSel, handle: this.handle, source: this },
      });
      const unhandled = D.dispatchEvent(event);
      if (!unhandled || event.detail.handled) {
        this.setBusy(true);
        return;
      }
      /* nobody added it: take the visitor to the buy box (cards mode: the picked card's add button) instead */
      const w = (this.cards && this.box && this.box.querySelector('[data-rv-add]')) || this.watch;
      w.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
      if (typeof w.focus === 'function') w.focus({ preventScroll: true });
    }
  }

  customElements.define('rv-sticky-atc', RvStickyAtc);
})();
